import { StyleSheet, View } from 'react-native';

import { Text } from '@/components/Text';
import {
  SERVER_STEP_STATE,
  TERMINAL_FAILURE_STATUSES,
  type Action,
  type Deal,
  type DealCta,
  type DealStep,
  type ServerStepState,
} from '@/types/deal';

import { Step, STEP_STATE, type StepState } from './Step';
import { isTerminalFailureStep, pickActiveStep } from './pickActiveStep';

export type DealStepperProps = {
  deal: Deal;
  action?: Action;
  /** Called when the active step's CTA is tapped, with the tapped cta (absent for legacy deals). */
  onCtaPress: (step: DealStep, cta?: DealCta) => void;
  ctaLoading?: boolean;
  /**
   * Deal is full this month and the user hasn't started (no Action). The
   * active step renders as Locked — no CTA, no tappable provider link — so it
   * never looks startable. Users already mid-flow are never blocked.
   */
  blocked?: boolean;
  testID?: string;
};

/**
 * True when the screen is waiting on the brand and there is nothing for the
 * user to tap: the server sent a state for every non-terminal step, none is
 * active, failed or overdue (overdue asks the user to double-check, so it is
 * something to do), at least one is reported or waiting, and the action is
 * not settled. The deal screen prints "You don't need to do anything right
 * now" under the stepper in that case. Against an older server (no
 * step_states) the answer is always false.
 */
export function nothingToDoRightNow(deal: Deal, action: Action | undefined): boolean {
  const states = action?.step_states;
  if (!states || action?.settled) return false;
  const covered = deal.steps_config.steps.every(
    (step) => step.terminal || states[step.key] !== undefined,
  );
  if (!covered) return false;
  const values = Object.values(states);
  const busy = values.some(
    (s) =>
      s.state === SERVER_STEP_STATE.Active ||
      s.state === SERVER_STEP_STATE.Failed ||
      s.state === SERVER_STEP_STATE.Overdue,
  );
  return (
    !busy &&
    values.some(
      (s) => s.state === SERVER_STEP_STATE.Reported || s.state === SERVER_STEP_STATE.Waiting,
    )
  );
}

/**
 * Sequential stepper. The server is the source of truth for state:
 * - `action.step_completions` says which steps are done
 * - `action.active_step_key` says which step is currently Active
 * - `action.status` (rejected/expired) drives the terminal-failure
 *   short-circuit
 *
 * Mobile is a thin renderer. Steps before the active key are
 * Completed; the active key is Active; everything after is Locked.
 * Terminal-failure-gated steps (rejected/expired) are hidden from the
 * visible list unless they're the active one — they re-appear via the
 * terminal short-circuit at the top.
 */
export function DealStepper({
  deal,
  action,
  onCtaPress,
  ctaLoading,
  blocked = false,
  testID,
}: DealStepperProps) {
  const steps = deal.steps_config.steps;
  const activeStep = pickActiveStep(steps, action);

  if (activeStep && isTerminalFailureStep(activeStep, action)) {
    return (
      <View style={styles.container} testID={testID}>
        <Step
          position={steps.indexOf(activeStep) + 1}
          name={activeStep.name}
          description={activeStep.description}
          state={STEP_STATE.Failed}
          ctas={activeStep.ctas}
          linkUrl={activeStep.link_url}
          linkLabel={activeStep.link_label}
          callout={activeStep.callout}
          testID="deal-stepper-step-terminal"
        />
      </View>
    );
  }

  // Schema drift safety: server set an active_step_key but it doesn't
  // resolve to any step in the current steps_config (deal redeployed,
  // step renamed/removed). Render an explicit empty state instead of
  // silently locking the entire stepper.
  // The server's per-step states must cover the active step; when they don't
  // (a re-authored deal between two responses), every step would fail closed
  // to Locked with nothing to tap, so say what to do instead.
  const serverStatesMissActive =
    action?.step_states !== undefined &&
    activeStep !== undefined &&
    !activeStep.terminal &&
    action.step_states[activeStep.key] === undefined;
  if ((action?.active_step_key && !activeStep) || serverStatesMissActive) {
    return (
      <View style={styles.container} testID={testID}>
        <Text variant="caption" color="textMuted">
          This deal was updated. Pull to refresh.
        </Text>
      </View>
    );
  }

  const serverStates = action?.step_states;
  const completedKeys = new Set(Object.keys(action?.step_completions ?? {}));
  const activeKey = activeStep?.key;

  // Hide terminal-failure-gated steps from the visible list when not
  // active — they pollute the stepper with negative outcomes the user
  // shouldn't read about until they actually happen.
  const visibleSteps = steps.filter((step) => step.key === activeKey || !isFailureGated(step));

  return (
    <View style={styles.container} testID={testID}>
      {visibleSteps.map((step, idx) => {
        const serverState = serverStates?.[step.key];
        // Smart server, dumb client: when the server sent step_states at all,
        // it is the only authority. A step it left out fails closed (Locked)
        // rather than falling back to the client-side derivation, which could
        // expose a CTA the server did not mean to show. The legacy derivation
        // runs only against a server that predates step_states entirely.
        const rawState = serverStates
          ? serverState
            ? fromServerState(serverState.state)
            : STEP_STATE.Locked
          : stateFor(step, activeKey, completedKeys);
        // A full deal locks the active step for a not-yet-started user: present
        // it as Locked and strip the CTA + provider link so nothing reads as
        // tappable. Everything else renders normally.
        const lockedByCap = blocked && rawState === STEP_STATE.Active;
        const state = lockedByCap ? STEP_STATE.Locked : rawState;
        const interactive = rawState === STEP_STATE.Active && !lockedByCap;
        const stepNumber = steps.indexOf(step) + 1;
        return (
          <Step
            key={step.key}
            position={stepNumber}
            name={step.name}
            description={step.description}
            state={state}
            // A capacity-locked active step strips all actions so nothing reads
            // as tappable (same reason link_url is stripped below).
            ctas={lockedByCap ? undefined : step.ctas}
            ctaLabel={interactive ? step.cta_label : undefined}
            onCtaPress={interactive ? (cta) => onCtaPress(step, cta) : undefined}
            ctaLoading={interactive ? ctaLoading : undefined}
            linkUrl={lockedByCap ? undefined : step.link_url}
            linkLabel={step.link_label}
            callout={step.callout}
            waitingDescription={step.waiting_description}
            provenanceLabel={serverState?.label}
            provenanceAt={serverState?.at}
            provenanceAtLabel={serverState?.at_label}
            isLast={idx === visibleSteps.length - 1}
            testID={`deal-stepper-step-${step.key}`}
          />
        );
      })}
    </View>
  );
}

/**
 * Deal Steps v2 (smart server, dumb client): the server already decided each
 * step's state (`action.step_states[key].state`); this only maps its
 * vocabulary onto ours. An unknown value from a newer server degrades to
 * Locked rather than crashing.
 */
function fromServerState(state: ServerStepState): StepState {
  switch (state) {
    case SERVER_STEP_STATE.Confirmed:
      return STEP_STATE.Confirmed;
    case SERVER_STEP_STATE.Reported:
      return STEP_STATE.Reported;
    case SERVER_STEP_STATE.Overdue:
      return STEP_STATE.Overdue;
    case SERVER_STEP_STATE.Completed:
      return STEP_STATE.Completed;
    case SERVER_STEP_STATE.Active:
      return STEP_STATE.Active;
    case SERVER_STEP_STATE.Waiting:
      return STEP_STATE.Waiting;
    case SERVER_STEP_STATE.Failed:
      return STEP_STATE.Failed;
    default:
      return STEP_STATE.Locked;
  }
}

/**
 * Legacy derivation, used only when the server sent no `step_states` (an
 * older backend, or a cached pre-v2 payload). Kept byte-for-byte so old
 * payloads render exactly as before.
 *
 * The current (server-picked) step is `Waiting` when it auto-completes
 * via webhook (`complete_on_event` is set) — the user has no CTA and
 * is waiting on the server. Otherwise it's `Active` (user-actionable
 * step, with or without a `cta_label` — without one we still render
 * the active bullet, just no button).
 */
function stateFor(
  step: DealStep,
  activeKey: string | undefined,
  completedKeys: Set<string>,
): StepState {
  if (completedKeys.has(step.key)) return STEP_STATE.Completed;
  if (step.key === activeKey) {
    return step.complete_on_event ? STEP_STATE.Waiting : STEP_STATE.Active;
  }
  return STEP_STATE.Locked;
}

function isFailureGated(step: DealStep): boolean {
  const statuses = step.active_when?.status ?? [];
  if (statuses.length === 0) return false;
  return statuses.every((s) => TERMINAL_FAILURE_STATUSES.includes(s));
}

const styles = StyleSheet.create({
  container: {
    // Each Step row owns its own top/bottom spacing so the connector
    // line between bullets stays continuous (no gap break).
  },
});
